import React from 'react'

import { BlockContainer } from '@Components/BlockContainer'
import { Link } from '@Components/Link'
import { Tiles } from '@Components/Tiles'
import { TilesCardLook } from '@Components/Tiles/Card/Look'
import { getAutomaticV2TileSettings, useTileSettings } from '@Components/Tiles/settings'
import { BlockAppearance, BlockCommon, LookTile, Maybe, PagePresentation, PageType, Scalars } from '@Generated/graphql'
import { getPins } from '@Globals/CMS/LookHelpers'
import { useCMSModalOpen } from '@Globals/CMS/Modal/Hook'
import { Image } from '@Globals/Image'
import { useTranslation } from '@Globals/Translations'

import styles from './Looks.module.scss'

// Image loader hint for v2 cards; mirrors the size CSS vars in TilesV2.module.scss.
const v2SizeToPx: Record<string, number> = {
  auto: 370,
  large: 370,
  medium: 300,
  small: 168,
  xsmall: 120,
}

export const Looks = ({
  appearance,
  common,
  id,
  looks,
  noPadding = false,
  presentation,
  settings,
  title,
  titleOverride,
}: {
  appearance?: Maybe<BlockAppearance>
  common?: Maybe<BlockCommon>
  id?: string
  looks: LookTile[]
  noPadding?: boolean
  presentation?: PagePresentation
  settings?: Maybe<Scalars['JSON']>
  title?: string
  titleOverride?: React.ReactNode
}) => {
  const parsedSettings = useTileSettings(settings)
  const openModal = useCMSModalOpen()
  const { t } = useTranslation()

  if (!looks || looks.length === 0) {
    return null
  }

  // v2 `automatic` opts into count-based settings, like v1's hardcoded look lists
  // (getAutomaticTileSettings in graph-api) — but per block instead of always
  const tileSettings = parsedSettings.version === '2' && parsedSettings.automatic
    ? { ...parsedSettings, ...getAutomaticV2TileSettings(looks.length) }
    : parsedSettings

  const isV2 = tileSettings.version === '2'

  // the single-look presentation (common beside the tile, neutral background): always-on
  // in v1; in v2 governed by the `single_look` setting (default on), independent of the
  // authored tile settings, so faceted blocks can opt out
  const singleLookPresentation = looks.length === 1 && (!isV2 || tileSettings.singleLook)
  let sizesHint = tileSettings.width ? `${tileSettings.width}px` : undefined
  if (isV2) {
    const sizePx = v2SizeToPx[tileSettings.size] || v2SizeToPx.large

    if (tileSettings.columns !== 'auto') {
      // fixed columns render 1/N-of-container tracks, which on wide screens far exceed
      // the size preset (columns 2 ≈ 700px+ where the preset says 300) — hint the
      // viewport share instead, mirroring the track CSS in TilesV2.module.scss:
      // sliders floor at the size preset; the grid ladder caps columns per breakpoint
      const columns = Number(tileSettings.columns) || 1
      const share = (n: number) => `${Math.round(100 / n)}vw`
      sizesHint = tileSettings.enableSlider
        ? `(min-width: ${columns * sizePx}px) ${share(columns)}, ${sizePx}px`
        : [
          `(min-width: 1600px) ${share(columns)}`,
          `(min-width: 1024px) ${share(Math.min(columns, 4))}`,
          `(min-width: 768px) ${share(Math.min(columns, 3))}`,
          share(Math.min(columns, 2)),
        ].join(', ')
    } else {
      // fluid tracks hold ~the size preset (auto-fill never stretches with count)
      sizesHint = `${sizePx}px`
    }
  }

  return (
    <BlockContainer
      appearance={{
        ...appearance || {},
        backgroundColor: singleLookPresentation ? 'var(--neutral-100)' : appearance?.backgroundColor,
      }}
      className={styles.blockContainer}
      common={{
        ...common,
        text: singleLookPresentation
          ? {
            key: 'common:discover_get_the_look',
            value: {
              text: t('common:discover_get_the_look'),
              type: 'p',
            },
          }
          : (common?.text || undefined),
        title: title || common?.title || '',
        titleElement: common?.titleElement || 'h2',
        position: singleLookPresentation ? 'left' : (common?.position || 'top'),
      }}
      commonWidth={singleLookPresentation ? 'half' : undefined}
      id={id}
      noPadding={noPadding}
      presentation={presentation || PagePresentation.Full}
      titleOverride={titleOverride}
    >
      <Tiles settings={tileSettings}>
        {looks.map(look => (
          <TilesCardLook
            key={look.id}
            ambassadorHandle={look.ambassadorHandle || undefined}
            cardAspectRatio={isV2 ? tileSettings.aspectRatio : tileSettings.cardAspectRatio}
            cardSize={tileSettings.cardSize}
            cardVariant={tileSettings.cardVariant}
            image={look.asset as Image}
            onClick={tileSettings.action === 'drawer' || !look.url
              ? (e) => {
                e.preventDefault()
                e.stopPropagation()

                openModal(PageType.Look, look.id!, 'inherit', presentation || PagePresentation.Sidebar)
              }
              : undefined}
            pins={getPins(look, undefined, presentation || PagePresentation.Sidebar)}
            presentation={presentation || PagePresentation.Sidebar}
            render={tileSettings.action === 'page' && look.url
              ? props => (
                <Link href={look.url!} {...props} />
              )
              : undefined}
            renderButtonAsText={Boolean(tileSettings.action === 'page' && look.url)}
            sizes={sizesHint}
            version={tileSettings.version}
          />
        ))}
      </Tiles>
    </BlockContainer>
  )
}
